import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';

// Types
export interface LockInfo {
  lockKey: string;
  username: string;
  expiresAt: number;
  studentId: string;
  dimensionId?: number;
  skillId?: number;
}

export interface PendingChange {
  requestId: string;
  studentId: string;
  dimensionId: number;
  skillId: number;
  levelIndex: number;
  action: 'update' | 'remove';
  timestamp: number;
  attempts: number;
  maxAttempts: number;
  nextRetry: number;
}

export interface SkillUpdate {
  studentId: string;
  dimensionId: number;
  skillId: number;
  levelIndex: number;
  action?: 'update' | 'remove';
  expectedVersion?: number;
}

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: number;
  autoHide?: boolean;
  duration?: number;
}

export interface LockContext {
  studentId: string;
  dimensionId?: number;
  skillId?: number;
  username?: string;
}

// Context interface - Simplified HTTP-only version
export interface SocketContextType {
  socket: null; // Always null - no WebSocket
  isConnected: boolean; // Always true for HTTP
  isAuthenticated: boolean;
  activeLocks: Map<string, LockInfo>;
  pendingChanges: Map<string, PendingChange>;
  notifications: Notification[];
  
  // Connection methods - simplified for HTTP
  connect: () => void;
  disconnect: () => void;
  reconnect: () => void;
  
  // Lock management - optimistic (always allow)
  requestLock: (context: LockContext) => Promise<boolean>;
  releaseLock: (lockKey: string) => Promise<boolean>;
  
  // Skill updates - handled via HTTP batch save
  updateSkill: (update: SkillUpdate) => Promise<boolean>;
  batchUpdateSkills: (updates: SkillUpdate[]) => Promise<boolean>;
  
  // Room management - no-op for HTTP
  joinCourse: (course: string) => void;
  leaveCourse: (course: string) => void;
  
  // Notification management
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotification: (id: string) => void; // Backward compatibility alias
  clearNotifications: () => void;
  
  // Statistics - placeholder
  getConnectionStats: () => any;
}

// Create context
const SocketContext = createContext<SocketContextType | null>(null);

// Hook to use socket context
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// Provider props
export interface SocketProviderProps {
  children: ReactNode;
  serverUrl?: string; // Not used in HTTP-only mode
}

// HTTP-only Provider implementation
export const SocketProvider: React.FC<SocketProviderProps> = ({ 
  children,
  serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000'
}) => {
  // State - simplified for HTTP-only mode
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeLocks] = useState<Map<string, LockInfo>>(new Map());
  const [pendingChanges] = useState<Map<string, PendingChange>>(new Map());
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Helper function to generate notification ID
  const generateNotificationId = useCallback(() => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }, []);

  // Add notification
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: generateNotificationId(),
      timestamp: Date.now()
    };

    setNotifications(prev => [newNotification, ...prev.slice(0, 4)]); // Keep only 5 most recent

    // Auto-hide if specified
    if (notification.autoHide !== false) {
      const duration = notification.duration || 5000;
      setTimeout(() => {
        removeNotification(newNotification.id);
      }, duration);
    }
  }, [generateNotificationId]);

  // Remove notification
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);
  
  // Remove notification alias for backward compatibility
  const clearNotification = useCallback((id: string) => {
    removeNotification(id);
  }, [removeNotification]);

  // Connection methods - HTTP mode (always connected)
  const connect = useCallback(() => {
    console.log('🔗 HTTP-only mode: Connection simulated');
    setIsAuthenticated(true);
    
    addNotification({
      type: 'success',
      title: 'Coordinación HTTP',
      message: 'Sistema configurado para coordinación HTTP optimizada',
      autoHide: true,
      duration: 3000
    });
  }, [addNotification]);

  const disconnect = useCallback(() => {
    console.log('🔗 HTTP-only mode: Disconnection simulated');
    setIsAuthenticated(false);
  }, []);

  const reconnect = useCallback(() => {
    console.log('🔗 HTTP-only mode: Reconnection simulated');
    connect();
  }, [connect]);

  // Lock management - Optimistic locking (always allow)
  const requestLock = useCallback(async (context: LockContext): Promise<boolean> => {
    console.log('🔒 HTTP-only mode: Optimistic locking - always allow', context);
    return true; // Always succeed - rely on DataPersistenceService merge logic
  }, []);

  const releaseLock = useCallback(async (lockKey: string): Promise<boolean> => {
    console.log('🔓 HTTP-only mode: Lock release - no-op', lockKey);
    return true; // Always succeed
  }, []);

  // Skill updates - Handled via HTTP batch save only
  const updateSkill = useCallback(async (update: SkillUpdate): Promise<boolean> => {
    console.log('💾 HTTP-only mode: Individual skill updates handled via batch save', update);
    return true; // Always succeed - handled by SaveProgressModal
  }, []);

  const batchUpdateSkills = useCallback(async (updates: SkillUpdate[]): Promise<boolean> => {
    console.log('📦 HTTP-only mode: Batch updates handled via SaveProgressModal', updates.length);
    return true; // Always succeed - handled by SaveProgressModal
  }, []);

  // Room management - no-op for HTTP
  const joinCourse = useCallback((course: string) => {
    console.log('🏫 HTTP-only mode: Course join - no-op', course);
  }, []);

  const leaveCourse = useCallback((course: string) => {
    console.log('🏫 HTTP-only mode: Course leave - no-op', course);
  }, []);

  // Statistics - placeholder
  const getConnectionStats = useCallback(() => {
    return {
      mode: 'HTTP-only',
      isConnected: true,
      isAuthenticated,
      activeLocks: activeLocks.size,
      pendingChanges: pendingChanges.size,
      notifications: notifications.length
    };
  }, [isAuthenticated, activeLocks.size, pendingChanges.size, notifications.length]);

  // Context value
  const contextValue = useMemo(() => ({
    socket: null, // Always null in HTTP-only mode
    isConnected: true, // Always true in HTTP-only mode
    isAuthenticated,
    activeLocks,
    pendingChanges,
    notifications,
    
    // Connection methods
    connect,
    disconnect,
    reconnect,
    
    // Lock management
    requestLock,
    releaseLock,
    
    // Skill updates
    updateSkill,
    batchUpdateSkills,
    
    // Room management
    joinCourse,
    leaveCourse,
    
    // Notification management
    addNotification,
    removeNotification,
    clearNotification, // Backward compatibility alias
    clearNotifications,
    
    // Statistics
    getConnectionStats
  }), [
    isAuthenticated,
    activeLocks,
    pendingChanges,
    notifications,
    connect,
    disconnect,
    reconnect,
    requestLock,
    releaseLock,
    updateSkill,
    batchUpdateSkills,
    joinCourse,
    leaveCourse,
    addNotification,
    removeNotification,
    clearNotification,
    clearNotifications,
    getConnectionStats
  ]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// Export context for direct access if needed
export { SocketContext };